import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ChevronRight,
  Compass,
  BarChart3,
  Calendar,
  Shield,
  Swords,
  MapPin,
  ArrowRight,
  Star,
  Sparkles,
  Clock,
  Eye,
  Target,
  Mail,
  Grid3X3
} from 'lucide-react';

const C = {
  navy: '#1B2838',
  deepBlue: '#1E3A5F',
  red: '#C41E3A',
  redLight: '#E8475C',
  gold: '#C89B3C',
  goldLight: '#E8C872',
  beige: '#F4EFE6',
  dark: '#151B26',
  card: '#1E2636',
  cardBorder: '#2A3548',
};

/* Countdown Timer */
const CountdownTimer = () => {
  const launchDate = new Date(Date.now() + 14 * 86400000);
  const [time, setTime] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const tick = () => {
      const diff = launchDate - new Date();
      if (diff <= 0) return;
      setTime({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex gap-4 md:gap-6 justify-center" data-testid="countdown-timer">
      {Object.entries(time).map(([label, val]) => (
        <div key={label} className="text-center">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl flex items-center justify-center text-3xl md:text-4xl font-bold"
            style={{ background: `${C.red}15`, color: C.redLight, border: `1px solid ${C.red}30` }}>
            {String(val).padStart(2, '0')}
          </div>
          <p className="text-xs mt-2 uppercase tracking-widest" style={{ color: '#667' }}>{label}</p>
        </div>
      ))}
    </div>
  );
};

/* Feature Card */
const FeatureCard = ({ icon: Icon, title, desc, accent }) => (
  <Card className="border-0 group cursor-pointer hover:-translate-y-1 transition-all duration-500"
    style={{ background: C.card, border: `1px solid ${C.cardBorder}` }}>
    <CardContent className="p-6">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
        style={{ background: `${accent}15` }}>
        <Icon className="w-6 h-6" style={{ color: accent }} />
      </div>
      <h3 className="text-white font-semibold text-lg mb-2">{title}</h3>
      <p className="text-sm leading-relaxed" style={{ color: '#8899aa' }}>{desc}</p>
    </CardContent>
  </Card>
);

/* Palace Cell */
const PalaceCell = ({ direction, element, stem, stemPinyin, door, doorLabel, star, starLabel, deity, deityLabel, num, colors }) => (
  <div className="rounded-lg p-2.5 relative group transition-all hover:scale-[1.02]"
    style={{ background: C.card, border: `1px solid ${C.cardBorder}` }}>
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-[9px] font-bold tracking-wider" style={{ color: C.gold }}>{direction}</span>
      <span className="text-[9px]" style={{ color: '#556' }}>{element}</span>
    </div>
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-lg font-serif" style={{ color: colors.stem }}>{stem}</span>
        <span className="text-[10px]" style={{ color: '#889' }}>{stemPinyin}</span>
      </div>
      <div className="flex items-center justify-between text-[10px]">
        <span style={{ color: colors.door }}>{door} {doorLabel}</span>
        <span className="font-bold text-sm" style={{ color: C.gold }}>{num}</span>
      </div>
      <div className="flex items-center justify-between text-[10px]">
        <span style={{ color: colors.star }}>{star} {starLabel}</span>
      </div>
      <div className="text-[10px]" style={{ color: '#667' }}>
        {deity} {deityLabel}
      </div>
    </div>
  </div>
);

const QMDJComingSoon = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'qmdj' })
      });
      const data = await res.json();
      setSubmitMsg(data.message || "You're on the list!");
      setSubmitted(true);
      setEmail('');
    } catch {
      setSubmitMsg("Something went wrong. Please try again.");
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: C.dark, color: C.beige }}>

      {/* BG */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full opacity-[0.03]"
          style={{ background: `radial-gradient(circle, ${C.red}, transparent 70%)` }} />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full opacity-[0.02]"
          style={{ background: `radial-gradient(circle, ${C.deepBlue}, transparent 70%)` }} />
        <svg className="absolute inset-0 w-full h-full opacity-[0.012]">
          <line x1="0" y1="33%" x2="100%" y2="33%" stroke={C.gold} strokeWidth="0.3" />
          <line x1="0" y1="66%" x2="100%" y2="66%" stroke={C.gold} strokeWidth="0.3" />
          <line x1="33%" y1="0" x2="33%" y2="100%" stroke={C.gold} strokeWidth="0.3" />
          <line x1="66%" y1="0" x2="66%" y2="100%" stroke={C.gold} strokeWidth="0.3" />
        </svg>
      </div>

      {/* NAV */}
      <nav className="relative z-10 border-b" style={{ borderColor: '#ffffff08' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_6499a36b-d5eb-47ae-af32-473f14f7d9c5/artifacts/5qxxlvv6_ChatGPT%20Image%20Mar%2018%2C%202026%2C%2010_48_18%20AM.png"
              alt="QMDJ Logo"
              className="w-10 h-10 rounded-lg object-contain"
              style={{ background: '#fff' }}
            />
            <span className="font-semibold text-white text-lg tracking-wide">QMDJ<span style={{ color: C.redLight }}>.live</span></span>
          </div>
          <a href="#waitlist">
            <Button className="text-sm font-medium px-5" style={{ background: C.red, color: '#fff' }}>
              Join Waitlist
            </Button>
          </a>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative z-10 pt-20 pb-16 md:pt-28 md:pb-24" data-testid="hero-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-8"
            style={{ background: `${C.red}12`, border: `1px solid ${C.red}30` }}>
            <Sparkles className="w-3.5 h-3.5" style={{ color: C.redLight }} />
            <span className="text-xs tracking-widest uppercase" style={{ color: C.redLight }}>Coming Soon</span>
          </div>

          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_6499a36b-d5eb-47ae-af32-473f14f7d9c5/artifacts/5qxxlvv6_ChatGPT%20Image%20Mar%2018%2C%202026%2C%2010_48_18%20AM.png"
              alt="Qi Men Dun Jia - QMDJ.live"
              className="w-48 h-48 md:w-64 md:h-64 object-contain drop-shadow-2xl"
            />
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight mb-6">
            The Ancient Art of<br className="hidden sm:block" />
            <span className="font-serif italic" style={{ color: C.goldLight }}>Strategic Divination</span>
          </h1>

          <p className="text-lg md:text-xl mb-2" style={{ color: '#8899aa' }}>
            奇門遁甲 — Qi Men Dun Jia
          </p>
          <p className="text-base max-w-2xl mx-auto mb-4 leading-relaxed" style={{ color: '#8899aa' }}>
            The most powerful and secretive of the Three Arts of War.
            Decode the energy of time and space to make strategic decisions with cosmic precision.
          </p>
          <p className="text-sm max-w-xl mx-auto mb-10" style={{ color: '#667' }}>
            Our platform is launching soon. Be the first to access the future of Qi Men analysis.
          </p>

          <div className="flex flex-wrap gap-4 justify-center">
            <a href="#waitlist">
              <Button className="px-8 py-3 text-base font-medium" style={{ background: C.red, color: '#fff' }}>
                Join the Waitlist <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <a href="#waitlist">
              <Button className="px-8 py-3 text-base font-medium border"
                style={{ background: 'transparent', color: C.beige, borderColor: C.cardBorder }}>
                <Mail className="w-4 h-4 mr-2" /> Notify Me When Live
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* DASHBOARD PREVIEW */}
      <section className="relative z-10 pb-24" data-testid="dashboard-preview">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="rounded-2xl p-1" style={{ background: `linear-gradient(135deg, ${C.red}25, ${C.cardBorder}, ${C.deepBlue}40)` }}>
            <div className="rounded-xl overflow-hidden" style={{ background: C.dark }}>
              <img
                src="https://customer-assets.emergentagent.com/job_6499a36b-d5eb-47ae-af32-473f14f7d9c5/artifacts/hffxwoje_Screen%20Shot%202026-03-18%20at%2010.45.41%20am.png"
                alt="Qi Men Dun Jia Chart - 9 Palace Grid with Stems, Doors, Stars & Deities"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="relative z-10 py-24" data-testid="features-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest mb-4" style={{ color: C.redLight }}>Intelligence Platform</p>
            <h2 className="text-3xl md:text-4xl font-light text-white">
              Powerful <span className="font-serif italic" style={{ color: C.goldLight }}>Qi Men</span> Intelligence
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard icon={Grid3X3} title="9 Palace Chart Engine"
              desc="Generate complete Qi Men Dun Jia charts instantly. All 9 palaces with Stems, Doors, Stars, and Deities mapped to compass directions."
              accent={C.redLight} />
            <FeatureCard icon={Shield} title="8 Doors Analysis"
              desc="Open, Rest, Life, Harm, Solitude, Scenery, Death, and Fear — understand which doors are active and their influence on your decisions."
              accent={C.gold} />
            <FeatureCard icon={Star} title="9 Stars Mapping"
              desc="Track the positions of all nine stars including Compassion, Bravery, Glamour, and Advisor to reveal strategic opportunities."
              accent="#4A90D9" />
            <FeatureCard icon={Eye} title="8 Deities Tracking"
              desc="Monitor the eight spiritual forces — Chief, Snake, Moon, Harmony, Hook, Phoenix, Earth, and Heaven — and their cosmic influence."
              accent="#9C27B0" />
            <FeatureCard icon={Target} title="Strategic Forecasting"
              desc="Use Qi Men for business decisions, negotiations, travel planning, and identifying the optimal direction and timing for action."
              accent="#4CAF50" />
            <FeatureCard icon={Calendar} title="Hourly & Daily Charts"
              desc="Access real-time Qi Men charts that change every 2 hours. Perfect for time-sensitive decisions and strategic planning."
              accent={C.goldLight} />
          </div>
        </div>
      </section>

      {/* ABOUT SECTION */}
      <section className="relative z-10 py-24" data-testid="about-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs uppercase tracking-widest mb-4" style={{ color: C.redLight }}>The Three Arts of War</p>
          <h2 className="text-3xl md:text-4xl font-light text-white mb-8">
            Ancient Strategy, <span className="font-serif italic" style={{ color: C.goldLight }}>Modern Platform</span>
          </h2>
          <p className="text-base leading-relaxed mb-6" style={{ color: '#8899aa' }}>
            Qi Men Dun Jia (奇門遁甲), known as <span className="text-white font-medium">"Mysterious Gates Escaping Technique"</span>, is the most powerful of the Three Arts of War in Chinese metaphysics. Historically used by military strategists and emperors, it analyzes the alignment of <span className="text-white font-medium">time, space, and cosmic energy</span> through a sophisticated system of palaces, doors, stars, and deities.
          </p>
          <p className="text-base leading-relaxed" style={{ color: '#8899aa' }}>
            Our platform transforms this ancient strategic system into a <span className="text-white font-medium">modern analytical tool</span>, making it accessible for business decisions, personal planning, and strategic forecasting.
          </p>

          {/* Key Elements */}
          <div className="flex flex-wrap justify-center gap-4 mt-12">
            {[
              { ch: '門', name: '8 Doors', color: C.redLight },
              { ch: '星', name: '9 Stars', color: C.gold },
              { ch: '神', name: '8 Deities', color: '#4A90D9' },
              { ch: '干', name: '10 Stems', color: '#4CAF50' },
              { ch: '宫', name: '9 Palaces', color: '#9C27B0' },
            ].map((e) => (
              <div key={e.name} className="text-center group cursor-default">
                <div className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-serif mx-auto mb-2 transition-transform group-hover:scale-110"
                  style={{ background: `${e.color}15`, color: e.color, border: `1px solid ${e.color}30` }}>
                  {e.ch}
                </div>
                <p className="text-xs" style={{ color: '#778' }}>{e.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SNEAK PEEK */}
      <section className="relative z-10 py-24" data-testid="sneak-peek-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-widest mb-4" style={{ color: C.redLight }}>Sneak Peek</p>
            <h2 className="text-3xl md:text-4xl font-light text-white">
              What You'll <span className="font-serif italic" style={{ color: C.goldLight }}>Decode</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Day Stem', main: '戊', sub: 'Wu – Yang Earth', el: 'Earth Element', color: '#8B7355' },
              { label: 'Active Door', main: '生', sub: 'Sheng – Life', el: 'Auspicious', color: '#4CAF50' },
              { label: 'Active Star', main: '心', sub: 'Xin – Compassion', el: 'Favorable', color: C.gold },
              { label: 'Active Deity', main: '合', sub: 'He – Harmony', el: 'Supportive', color: '#4A90D9' },
            ].map((card) => (
              <div key={card.label} className="rounded-xl p-5 text-center group hover:-translate-y-1 transition-all duration-500"
                style={{ background: C.card, border: `1px solid ${C.cardBorder}` }}>
                <p className="text-[10px] uppercase tracking-widest mb-4" style={{ color: '#556' }}>{card.label}</p>
                <div className="text-4xl font-serif mb-2" style={{ color: card.color }}>{card.main}</div>
                <p className="text-white text-sm font-medium">{card.sub}</p>
                <p className="text-xs mt-2" style={{ color: '#667' }}>{card.el}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COUNTDOWN + WAITLIST */}
      <section className="relative z-10 py-28" id="waitlist" data-testid="waitlist-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs uppercase tracking-widest mb-4" style={{ color: C.redLight }}>Launching Soon</p>
          <h2 className="text-3xl md:text-4xl font-light text-white mb-10">
            The Future of <span className="font-serif italic" style={{ color: C.goldLight }}>Qi Men Analysis</span>
          </h2>

          <CountdownTimer />

          <p className="mt-10 mb-8 text-base" style={{ color: '#8899aa' }}>
            We are preparing something powerful for the Qi Men Dun Jia community.<br />
            Join the waitlist to get early access.
          </p>

          {submitted ? (
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-xl"
              style={{ background: '#4CAF5015', border: '1px solid #4CAF5030', color: '#4CAF50' }}>
              <Star className="w-5 h-5" />
              <span className="font-medium">{submitMsg}</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="waitlist-form">
              <Input type="email" placeholder="Enter your email" value={email}
                onChange={(e) => setEmail(e.target.value)} required
                className="flex-1 h-12 text-white border-0"
                style={{ background: C.card, borderColor: C.cardBorder }}
                data-testid="waitlist-email" />
              <Button type="submit" disabled={submitting} className="h-12 px-8 font-medium"
                style={{ background: C.red, color: '#fff' }}
                data-testid="waitlist-submit">
                {submitting ? 'Joining...' : 'Join Waitlist'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative z-10 border-t py-12" style={{ borderColor: '#ffffff08' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <div className="flex items-center gap-2 justify-center md:justify-start mb-1">
                <Compass className="w-5 h-5" style={{ color: C.redLight }} />
                <span className="text-white font-semibold">QMDJ.live</span>
              </div>
              <p className="text-xs" style={{ color: '#556' }}>Powered by Life Decoder</p>
            </div>
            <div className="flex items-center gap-6">
              {['About', 'Contact', 'Privacy Policy'].map((link) => (
                <a key={link} href="#" className="text-sm transition-colors hover:text-white" style={{ color: '#667' }}>{link}</a>
              ))}
            </div>
          </div>

          {/* Links */}
          <div className="mt-8 pt-6 flex flex-wrap justify-center gap-4" style={{ borderTop: '1px solid #ffffff08' }}>
            {[
              { name: 'Stevenyong.live', url: 'https://stevenyong.live' },
              { name: 'Bazi.live', url: 'https://bazi.live' },
              { name: 'QMDJ.live', url: 'https://qmdj.live' },
              { name: 'Fengshuiplotter.live', url: 'https://fengshuiplotter.live' },
              { name: 'Lifecode.live', url: 'https://lifecode.live' },
              { name: 'Lifetunes.live', url: 'https://lifetunes.live' },
              { name: 'Onlinetongshu.com', url: 'https://onlinetongshu.com' },
            ].map((link) => (
              <a key={link.name} href={link.url} target="_blank" rel="noopener noreferrer"
                className="text-xs transition-colors hover:text-white" style={{ color: C.redLight }}>
                {link.name}
              </a>
            ))}
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs" style={{ color: '#445' }}>Life Decoder &copy; {new Date().getFullYear()} QMDJ.live - All rights reserved</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default QMDJComingSoon;
